import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DinamixSkeletonTable from "@/components/skeleton/dinamixTableSkeleton";
import { Button } from "@/components/ui/button";
import { PencilLine, ArrowLeft, Download } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Services
import { getDebt, lastgetDebt } from "@/services/finance";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/finance-debt/"
)({
  component: FinanceDebt,
});

function FinanceDebt() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId"); // Get companyId from local storage
  const yearly = Number(localStorage.getItem("year")); // Get year from local storage
  const { company_financial_report } = useAccessPermission();

  // Redirect if no year in localStorage
  React.useEffect(() => {
    if (!yearly) {
      navigate({ to: "/report/finance/company/finance-debt/add" });
    }
  }, [yearly, navigate]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["debt", companyId],
    queryFn: () => getDebt(companyId as string, yearly),
  });
  const {
    data: lastdebt,
    isLoading: lastdebtLoading,
    isError: lastdebtIsError,
    error: lastdebtError,
  } = useQuery({
    queryKey: ["lastdebt", companyId, yearly],
    queryFn: () => lastgetDebt(companyId as string, yearly - 1),
    enabled: !!companyId && !!yearly,
  });
  React.useEffect(() => {
    if (!data?.data && !isLoading) {
      navigate({ to: "/report/finance/company/finance-debt/add" });
    }
  }, [data, navigate, isLoading]);
  const [unit, setUnit] = React.useState("1"); // Move this hook to the top

  if (isError) return <div>Error: {error.message}</div>;

  const formatDisplayNumber = (value: number) => {
    if (!value && value !== 0) return "-";

    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const formattedValue = (absValue / unit).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  const sumAmount = (debts) =>
    debts?.reduce((total, debt) => total + debt.amount, 0);

  const debtTypes = ["REVOLVING", "NON_REVOLVING", "EQUITY"];
  const debtsByType = debtTypes.reduce((acc, type) => {
    acc[type] =
      data?.data?.debtDetails?.filter((debt) => debt.debtType === type) || [];
    return acc;
  }, {});

  const totalAmounts = debtTypes?.reduce((acc, type) => {
    acc[type] = sumAmount(debtsByType[type]);
    return acc;
  }, {});

  const totalAmount = Object?.values(totalAmounts).reduce(
    (total, amount) => total + amount,
    0
  );
  // Last year

  const lastSumAmount = (debts) =>
    debts?.reduce((total, debt) => total + debt.amount, 0);

  const lastDebtsByType = debtTypes.reduce((acc, type) => {
    acc[type] =
      lastdebt?.data?.debtDetails?.filter((debt) => debt.debtType === type) ||
      [];
    return acc;
  }, {});

  const lastTotalAmounts = debtTypes?.reduce((acc, type) => {
    acc[type] = lastSumAmount(lastDebtsByType[type]);
    return acc;
  }, {});

  const lastTotalAmount = Object?.values(lastTotalAmounts).reduce(
    (total, amount) => total + amount,
    0
  );

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
              })
            }
            className="flex items-center gap-[4px]  mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {yearly}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/finance-debt/edit",
                    replace: true,
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md  p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <div>
            {isLoading || lastdebtLoading ? (
              <DinamixSkeletonTable />
            ) : (
              <Table className="mb-4">
                <TableHeader>
                  <TableRow className="[&>*]:whitespace-nowrap [&>*]:border [&>*]:font-bold [&>*]:text-black">
                    <TableHead className="border">ໜີ້ສິນ ແລະ ທຶນ</TableHead>
                    <TableHead className="border">
                      ມູນຄ່າຍັງເຫຼືອ(ປີນີ້) {yearly}
                    </TableHead>
                    <TableHead className="border">
                      ມູນຄ່າຍັງເຫຼືອ(ປີຜ່ານມາ) {yearly - 1}
                    </TableHead>

                    <TableHead className="border">ໝາຍເຫດ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {debtTypes.map((type, index) => (
                    <React.Fragment key={index}>
                      <TableRow className="font-bold cursor-pointer bg-[#216C4C]/20">
                        <TableCell className="border" colSpan={4}>
                          {type === "REVOLVING"
                            ? "ໜີ້ສິນໝູນວຽນ"
                            : type === "NON_REVOLVING"
                              ? "ໜີ້ສິນບໍ່ໝູນວຽນ"
                              : "ທຶນ"}
                        </TableCell>
                      </TableRow>
                      {debtsByType[type]?.map((debt, index) => (
                        <TableRow key={index} className="cursor-pointer">
                          <TableCell className="border">{debt?.name}</TableCell>
                          <TableCell className="text-right border">
                            {formatDisplayNumber(debt?.amount / unit)}
                          </TableCell>
                          {/* year */}
                          <TableCell className="text-right border">
                            {lastDebtsByType[type]?.find(
                              (lastDebt) => lastDebt.name === debt.name
                            )?.amount
                              ? formatDisplayNumber(
                                  lastDebtsByType[type].find(
                                    (lastDebt) => lastDebt.name === debt.name
                                  )!.amount
                                )
                              : "-"}
                          </TableCell>
                          <TableCell className="border">
                            {debt?.remark}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-100 [&>*]:text-black [&>*]:font-bold">
                        <TableCell className="border">
                          {type === "REVOLVING"
                            ? "ລວມຍອດ ໜີ້ສິນໝູນວຽນ ( I )"
                            : type === "NON_REVOLVING"
                              ? "ລວມຍອດ ໜີ້ສິນບໍ່ໝູນວຽນ ( II )"
                              : "ລວມຍອດທຶນຕົນເອງ ( III )"}
                        </TableCell>
                        <TableCell className="text-right border">
                          {formatDisplayNumber(totalAmounts[type] / unit)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {formatDisplayNumber(lastTotalAmounts[type] / unit) ||
                            "-"}
                        </TableCell>
                        <TableCell className="border"> </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="border" colSpan={3}>
                          {" "}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  <TableRow className="bg-gray-100 [&>*]:text-black [&>*]:font-bold">
                    <TableCell className="border">
                      ລວມຍອດໜີ້ສິນທັງໝົດ ( I + II + III )
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatDisplayNumber(totalAmount / unit)}
                    </TableCell>

                    <TableCell className="text-right border">
                      {formatDisplayNumber(lastTotalAmount / unit) || "-"}
                    </TableCell>
                    <TableCell className="border">-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
          {data?.data?.docFile
            ? company_financial_report.isDownload && (
                <FileHandleViewer fileUrl={data?.data?.docFile} />
              )
            : null}
        </CardContent>
      </Card>
    </section>
  );
}
